import { sum, keyBy, flatten, groupBy, sortBy, round, toArray } from "lodash";
import { OrderStateId, PackageTypeId, PaymentMethodId } from "./enum";
import { TmOrderType } from "../feature/tm/common";
import { ProjectMailOrderConditionDto, ProjectMailOrderConditionIfOperator, ProjectMailOrderConditionIfType } from "./types";

export interface TmPostageInfo {
    error?: string;
    freePostage?: ProjectMailOrderConditionDto;
    paidPostage?: {
        all?: ProjectMailOrderConditionDto;
        regular?: ProjectMailOrderConditionDto;
        parcelShop?: ProjectMailOrderConditionDto;
    };
}

export interface TmMoPostageInfo {
    error?: string;
    freePostage?: { fromAmount: number; missingAmount: number; };
    postage?: {
        all?: number;
        regular?: number;
        parcelShop?: number;
    };
    currentPostageAuto?: ShipmentDetailDto;
    postageChanged?: boolean;
}

export interface OrderCalculation {
    tmPostage: TmMoPostageInfo | null;
    gifts: GiftsInfo;
    detailsSum: number;
    error?: string;
    autos: ShipmentDetailDto[];
    discount: number;
    freePostage: boolean;
}

export interface GiftsInfo {
    giftGroups: GiftDisplayGroup[];
    gifts: ShipmentDetailDto[];
    giftsChanged: boolean;
    giftsAdded: boolean;
    giftsRemoved: boolean;
    removedGifts: ShipmentDetailDto[];
    areAnyGiftsUnselected: boolean;
}

export interface GiftDisplayGroup {
    name: string;
    amount: number;
    missingAmount: number;
    items: GiftDisplayGroupItem[];
}

export interface GiftDisplayGroupItem {
    selectionGroupItems: ProjectMailOrderConditionDto[];
    selectionGroupOtherItems: ProjectMailOrderConditionDto[];
    moCond: ProjectMailOrderConditionDto;
}

export interface ShipmentDetailDto {
    id: number;
    saleArticleId: number;
    code: string;
    name: string;
    quantity: number;
    price: number;
    origPrice?: number;
    baseQuantity: number;
    isPost: boolean;
    weight: number;
    isAuto?: boolean;
    autoSelect?: boolean;
    missingAmount?: number;
    quantityWhenSelected?: number;
    otherAutosInGroup?: number[];
    isEditable?: boolean;
    projectMailOrderConditionId?: number;
    packageType?: PackageTypeId | null;
}

export interface OrderDto {
    id: number | null;
    details: ShipmentDetailDto[];
    autos: ShipmentDetailDto[];
    deliveryMethodPlaceId: number | null;
    freePostage: boolean;
    starterArticles?: ShipmentDetailDto[];
    tmOrderType?: TmOrderType;
}

export interface SaleArticleDto {
    id: number;
    code: string;
    name: string;
    price: number;
    baseQuantity: number;
    isPost: boolean;
    weight: number;
    discounts: SaleArticleDiscountDto[] | null;
    packageType: PackageTypeId | null;
}

export interface SaleArticleDetailDto {
    id: number;
    code: string;
    name: string;
    price: number;
    baseQuantity: number;
    isPost: boolean;
    weight: number;
    discounts: SaleArticleDiscountDto[] | null;
    packageType: PackageTypeId | null;
}

export interface SaleArticleDiscountDto {
    amount: number;
    fromQuantity: number;
    toQuantity: number;
}

function getFreePostage(postages: ProjectMailOrderConditionDto[]) {
    return postages.find(p =>
        p.ifType === ProjectMailOrderConditionIfType.Amount &&
        p.ifOperator === ProjectMailOrderConditionIfOperator.MoreThanOrEqual &&
        p.ifDeliveryMethodPlaceSpecified == null &&
        p.addArticle?.isPost === true &&
        p.addArticle?.price === 0);
}

function getNonFreePostage(postages: ProjectMailOrderConditionDto[], freePostage: ProjectMailOrderConditionDto | undefined, ifDeliveryMethodPlaceSpecified: boolean | null) {
    const potentialResults = postages.filter(p =>
        p.ifType === ProjectMailOrderConditionIfType.Amount &&
        p.ifDeliveryMethodPlaceSpecified === ifDeliveryMethodPlaceSpecified &&
        p.addArticle?.isPost === true &&
        (p.addArticle?.price ?? 0) > 0);
    if (freePostage) {
        return potentialResults.find(p =>
            p.ifOperator === ProjectMailOrderConditionIfOperator.LessThan &&
            p.ifAmount === freePostage.ifAmount);
    }
    return potentialResults.find(p =>
        p.ifOperator === ProjectMailOrderConditionIfOperator.MoreThanOrEqual &&
        p.ifAmount === 0);
}

function filterCallCenterAndLanguage(moConds: ProjectMailOrderConditionDto[], callCenterId: number | null | undefined, language: string) {
    return moConds.filter(p => (!p.ifLanguage || p.ifLanguage === language) &&
        ((callCenterId == null && p.ifCallCenterId == null) || p.ifCallCenterId == null || p.ifCallCenterId === callCenterId));
}

export function getTmPostageInfo(moConds: ProjectMailOrderConditionDto[], callCenterId: number | null | undefined, language: string): TmPostageInfo {
    moConds = filterCallCenterAndLanguage(moConds, callCenterId, language);
    const result: TmPostageInfo = {};
    const allPostages = moConds.filter(p => p.addArticle?.isPost === true);
    const freePostage = getFreePostage(moConds);
    const paidPostagePs = getNonFreePostage(moConds, freePostage, true);
    const paidPostageRegular = getNonFreePostage(moConds, freePostage, false);
    const paidPostageAll = getNonFreePostage(moConds, freePostage, null);
    const isNonFreePostageValid = (paidPostagePs && paidPostageRegular && !paidPostageAll) ||
        (paidPostageAll && !paidPostageRegular && !paidPostagePs) ||
        (freePostage && freePostage.ifAmount === 0 && !paidPostageAll && !paidPostageRegular && !paidPostagePs);
    const remainingPostage = allPostages.filter(p =>
        p !== freePostage &&
        p !== paidPostagePs &&
        p !== paidPostageRegular &&
        p !== paidPostageAll);
    if (allPostages.length === 0 || !isNonFreePostageValid || remainingPostage.length > 0) {
        result.error = "The project has invalid postage settings.";
    } else {
        if (freePostage) {
            result.freePostage = freePostage;
        }
        if (paidPostageAll || paidPostageRegular || paidPostagePs) {
            result.paidPostage = {
                all: paidPostageAll,
                regular: paidPostageRegular,
                parcelShop: paidPostagePs,
            };
        }
    }
    return result;
}

function getTmMoPostageInfo(order: OrderDto, moConds: ProjectMailOrderConditionDto[], detailsSum: number, callCenterId: number | null | undefined, language: string): TmMoPostageInfo {
    if (order.tmOrderType !== TmOrderType.Mail) {
        return {};
    }
    const info = getTmPostageInfo(moConds, callCenterId, language);
    if (info.error) {
        return { error: info.error };
    }
    const orderInfo: TmMoPostageInfo = {
        freePostage: info.freePostage == null ? undefined : {
            fromAmount: info.freePostage.ifAmount,
            missingAmount: round(Math.max(0, info.freePostage.ifAmount - detailsSum), 2),
        },
        postage: info.paidPostage == null ? undefined : {
            all: info.paidPostage.all?.addArticle?.price,
            regular: info.paidPostage.regular?.addArticle?.price,
            parcelShop: info.paidPostage.parcelShop?.addArticle?.price,
        },
    };

    const currentPostage = orderInfo.freePostage?.missingAmount === 0 ? info.freePostage :
        (info.paidPostage?.all ?? (order.deliveryMethodPlaceId == null ? info.paidPostage?.regular : info.paidPostage?.parcelShop));
    if (currentPostage != null) {
        orderInfo.currentPostageAuto = {
            id: currentPostage.id,
            projectMailOrderConditionId: currentPostage.id,
            saleArticleId: currentPostage.addArticle!.saleArticleId,
            code: currentPostage.addArticle!.code,
            name: currentPostage.addArticle!.name,
            quantity: currentPostage.addQuantity,
            baseQuantity: currentPostage.addArticle!.baseQuantity,
            price: order.freePostage ? 0 : currentPostage.addArticle!.price,
            origPrice: currentPostage.addArticle!.price,
            weight: currentPostage.addArticle!.weight,
            isPost: true,
            isAuto: true,
            packageType: currentPostage.addArticle!.packageType,
        };
    }

    return orderInfo;
}

export function getGiftConditions(moConds: ProjectMailOrderConditionDto[], detailsSum: number | null = null) {
    return moConds.filter(p =>
        p.ifType === ProjectMailOrderConditionIfType.Amount &&
        p.ifOperator === ProjectMailOrderConditionIfOperator.MoreThanOrEqual &&
        p.addArticle?.isPost === false &&
        (p.addDisc === 100 || p.addArticle.price === 0) &&
        (detailsSum == null || p.ifAmount <= detailsSum));
}

export function getGiftGroups(moConds: ProjectMailOrderConditionDto[], detailsSum: number): GiftDisplayGroup[] {
    const arts = getGiftConditions(moConds);
    const displayGroups = groupBy(arts, p => [p.displayGroup, p.ifAmount]);
    const selectionGroups = groupBy(sortBy(arts, p => p.positionInDisplayGroup), p => p.selectionGroup);
    return sortBy(Object.values(displayGroups).map(group => ({
        name: group[0].displayGroup,
        amount: group[0].ifAmount,
        missingAmount: round(Math.max(0, group[0].ifAmount - detailsSum), 2),
        position: group[0].displayGroupPosition,
        items: sortBy(group.map(p => ({
            selectionGroupItems: selectionGroups[p.selectionGroup] ?? [p],
            selectionGroupOtherItems: p.selectionGroup === "" ? [] : selectionGroups[p.selectionGroup].filter(q => q !== p),
            moCond: p,
        })), [p => p.moCond.positionInDisplayGroup, p => p.moCond.id]),
    })), [p => p.position, p => p.amount]);
}

export function getDetailsSum(details: ShipmentDetailDto[]) {
    return round(sum(details.map(p => p.price * p.quantity)), 2);
}

function filterByDeliveryMethodPlaceSpecified(moConds: ProjectMailOrderConditionDto[], order: OrderDto) {
    return moConds.filter(p => p.ifDeliveryMethodPlaceSpecified == null || p.ifDeliveryMethodPlaceSpecified === (order.deliveryMethodPlaceId != null));
}

export function getOrderCalculation(order: OrderDto, moConds: { isLoaded: boolean; items: ProjectMailOrderConditionDto[]; }, projectDiscount: number | null | undefined, salesArticles: SaleArticleDto[], manualDiscount: number | null | undefined, callCenterId: number | null | undefined, language: string): OrderCalculation {
    moConds = { ...moConds, items: filterCallCenterAndLanguage(moConds.items, callCenterId, language) };
    const nonPostageMoConds = filterByDeliveryMethodPlaceSpecified(moConds.items, order).filter(p => p.addArticle?.isPost === false);
    const giftMoConds = getGiftConditions(nonPostageMoConds, null);
    const nonPostageMoCondsWithoutGifts = nonPostageMoConds.filter(p => !giftMoConds.some(g => g.id === p.id));
    const detailsWithPostage = order.details.concat(order.starterArticles ?? []);
    const details = detailsWithPostage.filter(p => !p.isPost);
    const detailsSum = getDetailsSum(details);
    const nonGiftAutos = getNonGiftAutos(order, nonPostageMoCondsWithoutGifts, detailsSum);
    const nonGiftAutosSum = getDetailsSum(nonGiftAutos);
    const detailsAndNonGiftAutosDiscount = getDetailsDiscount(details.concat(nonGiftAutos), salesArticles);
    const discount = manualDiscount ?? Math.min(detailsSum + nonGiftAutosSum, Math.max(projectDiscount ?? 0, detailsAndNonGiftAutosDiscount));
    const discountedItemsSum = detailsSum + nonGiftAutosSum - discount;
    const gifts = getGiftsInfo(order, giftMoConds, discountedItemsSum);
    const isTm = order.tmOrderType != null;
    const tmPostage = !isTm || !moConds.isLoaded ? null : getTmMoPostageInfo(order, moConds.items, discountedItemsSum, callCenterId, language);
    const autos = (tmPostage?.currentPostageAuto ? [tmPostage.currentPostageAuto] : [])
        .concat(nonGiftAutos)
        .concat(gifts.gifts)
        .concat(isTm ? [] : getNonTmPostageAutos(order, moConds.items, discountedItemsSum));
    const freePostage = autos.concat(detailsWithPostage).some(p => p.isPost && p.price === 0);
    return {
        detailsSum,
        tmPostage,
        gifts,
        error: tmPostage?.error,
        autos,
        discount,
        freePostage,
    };
}

function getNonTmPostageAutos(order: OrderDto, moConds: ProjectMailOrderConditionDto[], discountedItemsSum: number) {
    moConds = filterByDeliveryMethodPlaceSpecified(moConds.filter(p => p.addArticle?.isPost === true), order);
    return getNonGiftAutos(order, moConds, discountedItemsSum);
}

function getDetailsDiscount(details: ShipmentDetailDto[], salesArticles: SaleArticleDto[]) {
    const discounts = toArray(groupBy(details, p => p.saleArticleId))
        .map(p => ({ saleArticleId: p[0].saleArticleId, quantity: sum(p.map(q => q.quantity)) }))
        .map(p => {
            const article = salesArticles.find(q => q.id === p.saleArticleId);
            if (article == null) {
                return 0;
            }
            const discount = article.discounts?.find(d => d.fromQuantity <= p.quantity && d.toQuantity >= p.quantity);
            return discount ? discount.amount : 0;
        });
    return sum(discounts);
}

function getNonGiftAutos(order: OrderDto, moConds: ProjectMailOrderConditionDto[], detailsSum: number) {
    const detailsDict = keyBy(order.details, p => p.saleArticleId);
    return moConds.map<ShipmentDetailDto | null>(p => {
        const price = round(p.addArticle!.price * (100 - p.addDisc) / 100, 2);
        const auto = {
            id: p.id,
            projectMailOrderConditionId: p.id,
            saleArticleId: p.addArticle!.saleArticleId,
            code: p.addArticle!.code,
            name: p.addArticle!.name,
            baseQuantity: p.addArticle!.baseQuantity,
            price: order.freePostage && p.addArticle?.isPost === true ? 0 : price,
            origPrice: price,
            weight: p.addArticle!.weight,
            isPost: p.addArticle!.isPost,
            isAuto: true,
            packageType: p.addArticle!.packageType,
        }
        if (p.ifType === ProjectMailOrderConditionIfType.Article) {
            const detail = detailsDict[p.ifSaleArticleId!];
            if (detail == null || p.ifQuantity > detail.quantity) {
                return null;
            }
            return {
                ...auto,
                quantity: p.ifQuantity === -1 ? -p.addQuantity * detail.quantity : p.addQuantity,
            }
        } else if (p.ifType === ProjectMailOrderConditionIfType.Amount) {
            if ((p.ifOperator === ProjectMailOrderConditionIfOperator.MoreThanOrEqual && detailsSum >= p.ifAmount) ||
                (p.ifOperator === ProjectMailOrderConditionIfOperator.MoreThan && detailsSum > p.ifAmount) ||
                (p.ifOperator === ProjectMailOrderConditionIfOperator.Equals && detailsSum === p.ifAmount) ||
                (p.ifOperator === ProjectMailOrderConditionIfOperator.NotEquals && detailsSum !== p.ifAmount) ||
                (p.ifOperator === ProjectMailOrderConditionIfOperator.LessThanOrEqual && detailsSum <= p.ifAmount) ||
                (p.ifOperator === ProjectMailOrderConditionIfOperator.LessThan && detailsSum < p.ifAmount)) {
                return {
                    ...auto,
                    quantity: p.addQuantity,
                };
            } else {
                return null;
            }
        } else {
            return null;
        }
    }).filter(p => p != null) as ShipmentDetailDto[];
}

function getGiftsInfo(order: OrderDto, giftsMoConds: ProjectMailOrderConditionDto[], detailsSum: number): GiftsInfo {
    giftsMoConds = filterByDeliveryMethodPlaceSpecified(giftsMoConds, order);
    const oldGifts = (order.autos ?? []).filter(p => !p.isPost);
    const oldGiftsDict = keyBy(oldGifts, p => p.projectMailOrderConditionId!);
    const giftGroups = getGiftGroups(giftsMoConds, detailsSum);
    const newGifts = flatten(flatten(giftGroups.map(group => group.items.map(p => {
        const oldGift = oldGiftsDict[p.moCond.id];
        const isSelected = oldGift?.quantity > 0;
        const autoSelect = oldGift?.autoSelect ?? (isSelected || ((p.selectionGroupOtherItems.length === 0 || p.selectionGroupItems[0] === p.moCond) && order.id == null));
        const newQuantity =
            group.missingAmount > 0 ? 0 : // force unselected
                !isSelected && autoSelect == null && order.id != null ? 0 : // keep unselected
                    !isSelected && autoSelect == null && p.selectionGroupOtherItems.length > 0 ? 0 : // keep unselected
                        !isSelected && autoSelect === false ? 0 : // keep unselected
                            p.moCond.addQuantity; // select or keep selected
        const auto: ShipmentDetailDto = {
            id: p.moCond.id,
            projectMailOrderConditionId: p.moCond.id,
            saleArticleId: p.moCond.addArticle!.saleArticleId,
            code: p.moCond.addArticle!.code,
            name: p.moCond.addArticle!.name,
            quantity: newQuantity,
            baseQuantity: p.moCond.addArticle!.baseQuantity,
            price: 0,
            weight: p.moCond.addArticle!.weight,
            isPost: false,
            isAuto: true,
            autoSelect,
            quantityWhenSelected: p.moCond.addQuantity,
            packageType: p.moCond.addArticle!.packageType,
            otherAutosInGroup: p.selectionGroupOtherItems.map(q => q.id),
            missingAmount: group.missingAmount,
        };
        return {
            auto,
            selectionGroup: p.moCond.selectionGroup,
        };
    }))));
    const removedGifts = newGifts.filter(p => p.auto.quantity === 0 && oldGiftsDict[p.auto.projectMailOrderConditionId!]?.quantity > 0);
    const giftsAdded = newGifts.length > 0;
    const giftsRemoved = removedGifts.length > 0;
    const giftsChanged = giftsAdded || giftsRemoved;
    const giftSelectionGroups = Object.values(groupBy(newGifts, p => (p.selectionGroup ?? "").trim() === "" ? p.auto.projectMailOrderConditionId : "x" + p.selectionGroup));
    const areAnyGiftsUnselected = giftSelectionGroups.some(p => p[0].auto.missingAmount === 0 && !p.some(q => q.auto.quantity > 0));
    return { giftGroups, gifts: newGifts.map(p => p.auto), giftsChanged, giftsAdded, giftsRemoved, removedGifts: removedGifts.map(p => p.auto), areAnyGiftsUnselected };
}

export function isOrderEditable(orderStateId: OrderStateId | null | undefined, paymentMethodId: PaymentMethodId | null | undefined, isAdminRole: boolean) {
    return orderStateId != null && orderStateId !== OrderStateId.Shipped && ((paymentMethodId !== PaymentMethodId.Card) || (paymentMethodId === PaymentMethodId.Card && isAdminRole));
}